import React, { useCallback, useEffect, useState } from "react";
import { X, Copy, ExternalLink, Trash2, Link2, Loader2 } from "lucide-react";
import { api } from "@/platform/api";
import { Button } from "@/platform/components/primitives";
import { ReviewerLinkCopy } from "@/platform/app/ReviewerLinkCopy";

const DURATIONS = [["14", "14 days"], ["7", "7 days"], ["30", "30 days"], ["none", "No expiration"]];
const urlKey = (id) => `pinlayr_invite_url_${id}`;

function fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch (_e) { return null; }
}
function relTime(iso) {
  if (!iso) return null;
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function InviteReviewerModal({ project, onClose, onChanged }) {
  const [invites, setInvites] = useState(null);
  const [label, setLabel] = useState("");
  const [duration, setDuration] = useState("14");
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const load = useCallback(async () => {
    const res = await api.get(`/api/projects/${project.id}/reviewer-invites`);
    setInvites(res.invites || []);
  }, [project.id]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setCreating(true);
    try {
      const res = await api.post(`/api/projects/${project.id}/reviewer-invites`, { label: label.trim(), duration });
      const link = `${window.location.origin}/r/${res.token}`;
      try { localStorage.setItem(urlKey(res.invite.id), link); } catch (_e) { /* ignore */ }
      setCreated({ ...res, url: link });
      setLabel("");
      await load();
      onChanged?.();
    } catch (_e) { /* ignore */ }
    setCreating(false);
  };

  const revoke = async (id) => {
    try { await api.post(`/api/projects/${project.id}/reviewer-invites/${id}/revoke`); } catch (_e) { /* ignore */ }
    setConfirmId(null);
    await load();
    onChanged?.();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-5" data-testid="invite-reviewer-modal">
      <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" onClick={onClose} />
      <div className="relative flex max-h-[88vh] w-full max-w-[560px] flex-col rounded-2xl border border-[#242A38] bg-[#151A24] shadow-2xl">
        <div className="flex items-start justify-between p-6 pb-4">
          <div>
            <h2 className="text-[20px] font-semibold tracking-tight text-[#F4F5F8]">Invite a reviewer</h2>
            <p className="mt-1 text-[13.5px] text-[#98A0B0]">Share a secure review link. Reviewers can leave feedback without creating a Pinlayr account.</p>
          </div>
          <button onClick={onClose} className="text-[#6E7686] hover:text-[#AAB1BF]" aria-label="Close" data-testid="invite-close"><X className="h-5 w-5" /></button>
        </div>

        <div className="overflow-y-auto px-6 pb-6">
          {created ? (
            <div className="rounded-xl border border-[#2E7D46] bg-[#16A34A14] p-4" data-testid="invite-created">
              <p className="text-[13px] font-semibold text-[#4ADE80]">Review link ready</p>
              <div className="mt-2 flex items-center gap-2 rounded-lg bg-[#0E1016] p-2.5">
                <Link2 className="h-4 w-4 shrink-0 text-[#6E7686]" />
                <code className="flex-1 truncate text-[12.5px] text-[#E6E8EE]" data-testid="invite-created-url">{created.url}</code>
              </div>
              <div className="mt-3 flex items-center gap-4">
                <Button onClick={() => { navigator.clipboard?.writeText(created.url); }} className="sm:w-auto sm:px-5" data-testid="invite-copy-created"><Copy className="h-4 w-4" /> Copy link</Button>
                <a href={created.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#A99BFF] hover:underline" data-testid="invite-open-created"><ExternalLink className="h-4 w-4" /> Open link</a>
                <button onClick={() => setCreated(null)} className="ml-auto text-[13px] text-[#98A0B0] hover:text-[#F4F5F8]" data-testid="invite-create-another">Create another</button>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-[#242A38] bg-[#12151D] p-4">
              <label className="block text-[12px] font-semibold uppercase tracking-wider text-[#6E7686]">Reviewer name or email</label>
              <input value={label} onChange={(e) => setLabel(e.target.value.slice(0, 60))} placeholder="e.g. David, or david@client.com"
                className="mt-1.5 w-full rounded-xl border border-[#2A3040] bg-[#0E1016] px-3.5 py-2.5 text-[14px] text-[#F4F5F8] outline-none focus:border-[#6D5EF6]" data-testid="invite-label-input" />
              <p className="mt-1.5 text-[12px] text-[#6E7686]">Shown as the default identity on this link's access page. The reviewer can change it, and their choice sticks to the link.</p>
              <label className="mt-4 block text-[12px] font-semibold uppercase tracking-wider text-[#6E7686]">Access duration</label>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {DURATIONS.map(([v, l]) => (
                  <button key={v} onClick={() => setDuration(v)}
                    className={`rounded-lg px-3 py-1.5 text-[12.5px] font-medium transition-colors ${duration === v ? "bg-[#1C2130] text-[#F4F5F8]" : "bg-[#0E1016] text-[#6E7686] hover:text-[#AAB1BF]"}`}
                    data-testid={`invite-duration-${v}`}>{l}</button>
                ))}
              </div>
              <div className="mt-4"><Button onClick={create} loading={creating} className="sm:w-auto sm:px-6" data-testid="invite-create-btn">Create review link</Button></div>
            </div>
          )}

          <div className="mt-6">
            <p className="mb-2 text-[12px] font-semibold uppercase tracking-wider text-[#6E7686]">Reviewer links</p>
            {invites === null ? (
              <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[#6D5EF6]" /></div>
            ) : invites.length === 0 ? (
              <p className="rounded-xl border border-dashed border-[#242A38] py-6 text-center text-[13px] text-[#565E70]" data-testid="invite-empty">No reviewer links yet.</p>
            ) : (
              <div className="space-y-2" data-testid="invite-list">
                {invites.map((inv) => {
                  const active = inv.status === "active";
                  return (
                    <div key={inv.id} className="rounded-xl border border-[#242A38] bg-[#12151D] p-3.5" data-testid={`invite-row-${inv.id}`}>
                      <div className="flex items-center gap-2">
                        <span className="truncate text-[14px] font-medium text-[#E6E8EE]">{inv.label}</span>
                        <span className={`rounded-full px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide ${active ? "bg-[#16A34A22] text-[#4ADE80]" : inv.status === "expired" ? "bg-[#D9770622] text-[#FBBF24]" : "bg-[#64748B22] text-[#94A3B8]"}`}>{inv.status}</span>
                        <span className="ml-auto text-[11px] text-[#565E70]">
                          {inv.expiresAt ? `expires ${fmtDate(inv.expiresAt)}` : "no expiration"}
                          {inv.lastUsedAt ? ` · used ${relTime(inv.lastUsedAt)}` : ""}
                        </span>
                      </div>
                      {confirmId === inv.id ? (
                        <div className="mt-3 rounded-lg bg-[#0E1016] p-3">
                          <p className="text-[13px] font-medium text-[#F4F5F8]">Revoke this review link?</p>
                          <p className="mt-1 text-[12px] text-[#98A0B0]">Anyone using this link will lose access to the project. Existing comments will remain.</p>
                          <div className="mt-3 flex items-center gap-2">
                            <button onClick={() => revoke(inv.id)} className="rounded-lg bg-[#EB5A46] px-3.5 py-1.5 text-[12.5px] font-semibold text-white" data-testid={`invite-revoke-confirm-${inv.id}`}>Revoke</button>
                            <button onClick={() => setConfirmId(null)} className="text-[12.5px] text-[#98A0B0] hover:text-[#F4F5F8]">Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-2.5 flex items-center gap-4">
                          {active ? <ReviewerLinkCopy projectId={project.id} inv={inv} label="Copy" testid={`invite-copy-${inv.id}`} /> : <span className="text-[11px] text-[#565E70]">inactive</span>}
                          {active && <button onClick={() => setConfirmId(inv.id)} className="inline-flex items-center gap-1 text-[12px] font-medium text-[#F87171] hover:underline" data-testid={`invite-revoke-${inv.id}`}><Trash2 className="h-3.5 w-3.5" /> Revoke</button>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
